import { useState, useEffect } from "react";
import { ShoppingCart, Star, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getCombos } from "@/lib/content";
import { useCart } from "@/context/CartContext";
import type { Combo } from "@/types/content";
import { getOptimizedImageUrl } from "@/lib/utils";



const CombosSection = () => {
  const navigate = useNavigate();
  const { cartItems, addToCart, removeFromCart } = useCart();
  const [combos, setCombos] = useState<Combo[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    getCombos(setCombos).then(setCombos);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const defaultComboImage = "https://placehold.co/600x600/e6f0fa/3561a6?text=Combo+Pack";

  if (combos.length === 0) {
    return (
      <section id="combos" className="py-16 md:py-24 bg-[#FAF8F5] border-t border-border/40">
        <div className="container">
          <div className="animate-pulse h-48 rounded-none bg-muted" />
        </div>
      </section>
    );
  }

  return (
    <section id="combos" className="py-20 md:py-28 bg-[#FAF8F5] border-t border-border/40">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-12 lg:px-16">
        
        {/* Header */}
        <div className="text-center mb-16 md:mb-24 space-y-4">
          <p className="text-muted-foreground font-body font-light tracking-[0.25em] uppercase text-[10px] md:text-xs">CURATED PAIRINGS</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light text-foreground uppercase tracking-[0.3em] leading-relaxed">SPECIAL CURATED SETS</h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-xs sm:text-sm font-body font-light tracking-wide uppercase leading-relaxed">
            Thoughtfully compiled ensembles showcasing our signature silhouettes in complementary textures and tones.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 md:gap-16 max-w-5xl mx-auto px-1 sm:px-0">
          {combos.map((c) => {
            const inCart = cartItems.some(item => item.name === c.name);
            
            return (
              <div
                key={c.id ?? c.name}
                className="group flex flex-col bg-transparent overflow-hidden"
              >
                {/* Image Area */}
                <div className="relative aspect-[4/5] overflow-hidden bg-[#EAE5D9]/30 mb-6 flex items-center justify-center">
                  <img
                    src={getOptimizedImageUrl(c.image ?? defaultComboImage, isMobile)}
                    alt={c.name}
                    width="600"
                    height="750"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                    loading="lazy"
                  />
                  
                  {/* Badge */}
                  <span className="absolute top-4 left-4 rounded-none bg-[#FAF8F5]/90 backdrop-blur-sm px-3 py-1.5 text-[9px] font-body font-light text-[#171717] tracking-[0.2em] uppercase shadow-sm">
                    {c.badge.toUpperCase()}
                  </span>

                  {/* Overlay Title for placeholder visibility */}
                  {!c.image && (
                    <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                      <span className="text-xs font-body font-light text-foreground/30 tracking-widest uppercase leading-none">
                        {c.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Content Area */}
                <div className="flex flex-col flex-1">
                  <div className="flex justify-between items-start gap-4 mb-3">
                    <h3 className="text-sm md:text-base font-display font-light text-foreground uppercase tracking-[0.15em] leading-snug">
                      {c.name}
                    </h3>
                    <div className="text-right shrink-0">
                      <span className="text-sm md:text-base font-body font-light text-foreground">৳{c.price}</span>
                      {c.originalPrice && c.originalPrice > c.price && (
                        <span className="block text-[10px] font-body font-light text-muted-foreground/60 line-through mt-1">৳{c.originalPrice}</span>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-[11px] font-body font-light text-muted-foreground/80 leading-relaxed mb-8 line-clamp-2 uppercase tracking-wide">
                    {c.items.join(" • ")}
                  </p>

                  {/* Action Buttons */}
                  <div className="mt-auto grid grid-cols-2 gap-3">
                    {inCart ? (
                      <button
                        onClick={() => removeFromCart(c.name)}
                        className="bg-neutral-100 text-foreground py-3.5 rounded-none text-[10px] font-body font-light uppercase tracking-[0.2em] transition-colors hover:bg-red-50 hover:text-red-600 flex items-center justify-center gap-1.5"
                      >
                        <Check size={12} className="w-3 h-3" strokeWidth={1.5} />
                        IN BAG
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          addToCart(c.name, c.price);
                          toast.success(`${c.name} added to bag!`, {
                            position: "bottom-center",
                            style: { background: "#171717", color: "#fff", border: "none", borderRadius: "0px" }
                          });
                        }}
                        className="bg-transparent border border-border/80 text-foreground hover:border-[#171717] hover:bg-[#171717] hover:text-white py-3.5 rounded-none text-[10px] font-body font-light uppercase tracking-[0.2em] transition-all duration-300 flex items-center justify-center gap-1"
                      >
                        ADD TO BAG
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        if (!inCart) {
                          addToCart(c.name, c.price);
                        }
                        navigate("/checkout");
                      }}
                      className="bg-[#171717] border border-[#171717] text-white py-3.5 rounded-none text-[10px] font-body font-light uppercase tracking-[0.2em] transition-colors hover:bg-[#2A2A2A] hover:border-[#2A2A2A] shadow-none"
                    >
                      BUY NOW
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CombosSection;
